import { useState } from 'react';

import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { FREEZE_METHODS } from '@/constants';
import type { CellRendererParams } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type {
  MassInterimEmbryosInput,
  TOA_GetFertilizationsForFreezing,
} from '@/rtk-query.gql';

import { useAddMassInterimEmbryos } from '../../../services/freeze/hooks';
import { useFreezeContext } from '../hooks/useFreezeContext';
import { SLAUGHTER_HOUSE_SITES } from '../utils/constants';
import { MassEmbryoCreationDialog } from './MassEmbryoCreationDialog';

import './CreateEmbryoButton.css';

type CreateEmbryoButtonProps = {
  params: CellRendererParams<TOA_GetFertilizationsForFreezing>;
};

export const CreateEmbryoButton = ({ params }: CreateEmbryoButtonProps) => {
  const dispatch = useDispatch();
  const fixEmptyCaneCodes = useFeatureFlag('fix_empty_cane_codes');
  const { form } = useFreezeContext();
  const [showCreateEmbryoDialog, setShowCreateEmbryoDialog] =
    useState<boolean>();
  const [grade1, setGrade1] = useState(0);
  const [grade2, setGrade2] = useState(0);

  const isSlaughterHouse = SLAUGHTER_HOUSE_SITES.includes(
    params?.data?.opuSite as string,
  );

  const { mutateAsync: addMassIntEmbryos } = useAddMassInterimEmbryos();

  const handleCreateEmbryos = () => {
    const rowData = params.data;
    if (!params.context?.storageLocation) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Must choose storage location',
          sticky: false,
          closable: true,
        }),
      );
    } else if (!params.context?.freezeDay) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Must choose freeze day',
          sticky: false,
          closable: true,
        }),
      );
    } else {
      const tempEmbryoInput: MassInterimEmbryosInput = {
        fertID: rowData?.fertId as string,
        cane: fixEmptyCaneCodes ? rowData?.cane : '',
        g1Count: grade1,
        g2Count: grade2,
        freezeDay: params.context?.freezeDay?.toString() as string,
        storageLoc: params.context?.storageLocation as string,
        graderID: params.context?.grader as string,
        freezeTechID: params.context?.freezeTech as string,
        freezer: params?.context?.freezer as string,
      };

      addMassIntEmbryos({
        input: tempEmbryoInput,
        dtplus:
          rowData?.freezeMethod?.includes(FREEZE_METHODS.VITRIFICATION) ||
          rowData?.freezeMethod?.includes(FREEZE_METHODS.GLYCEROL)
            ? false
            : (form?.dt as boolean),
      });
    }
  };

  return (
    <>
      {isSlaughterHouse && (
        <>
          <div className={`create-embryo-button`}>
            <Button
              label="Create Embryos"
              onClick={() => setShowCreateEmbryoDialog(true)}
            />
          </div>
          <MassEmbryoCreationDialog
            visible={showCreateEmbryoDialog as boolean}
            onHide={() => setShowCreateEmbryoDialog(false)}
            grade1={grade1}
            grade2={grade2}
            onGrade1Change={setGrade1}
            onGrade2Change={setGrade2}
            onAccept={handleCreateEmbryos}
          />
        </>
      )}
    </>
  );
};
